import React from 'react';
import { PerigonArticle } from '../../services/agents/newsAgent';

interface NewsCarouselProps {
    articles: PerigonArticle[];
    isDarkMode: boolean;
}

export const NewsCarousel: React.FC<NewsCarouselProps> = ({ articles, isDarkMode }) => {
    if (!articles || articles.length === 0) {
        return (
            <div className={`flex items-center justify-center p-8 ${isDarkMode ? "text-slate-300" : "text-slate-500"}`}>
                <p>No news articles found</p>
            </div>
        );
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 1) return "Just now";
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return "1 day ago";
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
    };

    const cardClass = isDarkMode
        ? "bg-[#202020] border-white/5 hover:border-primary/60 hover:shadow-[0_26px_52px_-24px_rgba(8,8,12,0.9)]"
        : "bg-white/95 border-slate-200/80 shadow-[0_18px_54px_-28px_rgba(15,23,42,0.25)] hover:border-primary/40 hover:shadow-[0_28px_62px_-30px_rgba(59,130,246,0.35)] backdrop-blur";

    const badgeClass = isDarkMode
        ? "hover:bg-white/15"
        : "hover:bg-black/30";

    const iconButtonClass = isDarkMode
        ? "text-gray-200 hover:bg-white/10"
        : "text-slate-600 hover:bg-slate-200/70";

    const titleClass = isDarkMode
        ? "text-slate-100"
        : "text-slate-900";

    const timeTextClass = isDarkMode
        ? "text-slate-300"
        : "text-slate-600";

    const descTextClass = isDarkMode
        ? "text-slate-200"
        : "text-slate-700";

    const dividerClass = "via-primary/35";

    return (
        <div className="overflow-x-auto">
            <div className="flex gap-6 p-4 min-w-max">
                {articles
                    .filter(article => {
                        const description = article.description || article.summary || '';
                        const wordCount = description.trim().split(/\s+/).filter(word => word.length > 0).length;
                        return wordCount >= 10;
                    })
                    .map((article, idx) => {
                        const domain = (article.source?.domain || 'News').replace(/\.(com|io|news|net|org|co|in|ai|dev|ie|uk|us|ca|tech|edu|gov|tv)$/i, '');
                        const description = article.description || article.summary || 'No description available';

                        return (
                            <div
                                key={`${article.articleId}-${idx}`}
                                className={`group relative flex flex-col overflow-hidden rounded-3xl border transition-all duration-300 ${cardClass} w-[290px] sm:w-80 flex-shrink-0 h-max`}
                            >
                                <div className="p-0 flex flex-col">
                                    {/* Image Section */}
                                    <div className="relative rounded-3xl rounded-b-none p-3 transition-colors">
                                        <div className={`relative w-full aspect-[16/9] rounded-2xl overflow-hidden select-none bg-gradient-to-br ${isDarkMode ? "from-slate-800/70 via-slate-900/80 to-slate-900/60" : "from-slate-200/70 via-slate-300/60 to-slate-200/50"}`}>
                                            <img
                                                src={article.imageUrl || "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=300&fit=crop"}
                                                alt={article.title || "Untitled"}
                                                className="w-full h-full object-cover transition-all duration-300 cursor-pointer"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=300&fit=crop';
                                                }}
                                                onClick={() => window.open(article.url || '#', '_blank')}
                                            />
                                        </div>
                                        <div className="absolute bottom-4 left-4 overflow-hidden">
                                            <span className={`border-0 bg-transparent px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur rounded-md transition-colors ${badgeClass}`}>
                                                {domain}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-2.5 xs:p-4 bg-transparent">
                                        <div className="flex flex-col items-start gap-3 mb-4">
                                            <div className="flex w-full items-center gap-2 text-xs uppercase tracking-wide">
                                                <svg className="h-3.5 w-3.5 text-primary/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12,6 12,12 16,14"></polyline>
                                                </svg>
                                                <span className={`pr-2 py-1 rounded-full bg-transparent text-primary/90 font-medium ${timeTextClass}`}>
                                                    {formatDate(article.pubDate || new Date().toISOString())}
                                                </span>
                                                <span className="ml-auto text-[10px] font-semibold uppercase select-none tracking-[0.18em] text-primary/90 bg-primary/5 px-2 py-1 rounded-md">
                                                    {(article.country || 'GLOBAL').toUpperCase()}
                                                </span>
                                            </div>

                                            <div className="flex w-full items-start justify-between gap-2">
                                                <h3 className={`font-semibold text-[1.22rem] leading-tight line-clamp-3 transition-colors group-hover:text-primary flex-1 ${titleClass}`}>
                                                    {article.title || 'Untitled'}
                                                </h3>
                                                <button
                                                    onClick={() => window.open(article.url || '#', '_blank')}
                                                    className={`relative p-2 h-9 w-9 rounded-xl transition-colors ${iconButtonClass} flex-shrink-0`}
                                                    title="Open Article"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                                        <polyline points="15,3 21,3 21,9"></polyline>
                                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        <div className={`h-px w-full rounded-full bg-gradient-to-r from-transparent ${dividerClass} to-transparent mb-3`}></div>

                                        <div className="mb-2 max-h-[200px] overflow-y-auto">
                                            <p className={`leading-relaxed text-sm ${descTextClass}`}>
                                                {description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};
